/**
 * REST client for the Kredibility backend (`../kredibility-rest-server`).
 *
 * This is the hand-maintained, live client. The generated clients under
 * `services/ant-design-pro/` and `services/swagger/` are scaffolding left over
 * from the Ant Design Pro template and are not used by the product.
 *
 * Conventions every function here follows:
 * - Responses share one envelope: `{ success, errorCode?, errorMessage?, data? }`.
 *   `success: false` arrives with HTTP 200 and is turned into a thrown error by
 *   `errorThrower` in `src/requestErrorConfig.ts`.
 * - The `/api` prefix is stripped by the dev proxy (`config/proxy.ts`) and by
 *   nginx in production, so it is a routing marker rather than a real path segment.
 *   The `/gpt/` prefix used to reach the LLM service directly; both of its
 *   functions are now dead and the proxy entry is commented out in
 *   `config/proxy.ts` and `docker/default.conf`.
 * - The trailing `options` argument is spread into the request config, which is
 *   how callers pass `skipErrorHandler: true` to handle failures themselves.
 * - Listings paginate by cursor (`lastId`), not by page number.
 *
 * Functions are grouped by resource under the banners below; within a group the
 * order is read, then write.
 */
import { request } from '@umijs/max';

// ============================================================================
// AUTHENTICATION & SESSION
// Every call below opts out of the global error handler, because the auth
// screens render failures inline as field errors rather than as a toast.
// ============================================================================

/**
 * Performs user login via backend API `/api/v2/auth/login`
 *
 * Behavior
 * 1) Sends a POST request with login parameters (e.g. username/email + password) as JSON
 * 2) Expects backend response containing user info and issued access/refresh tokens
 * 3) Returns a typed response with `success` flag, optional `errorCode`/`errorMessage`, and `data`
 * 4) Automatically sets request headers to `application/json`
 * 5) `skipErrorHandler: true` means global error handler will not automatically intercept this request;
 *    consumer must handle errors explicitly
 *
 * @async
 * @function login
 * @param {DATA.LoginParams} body - login parameters, usually containing identifier (email/username) and password
 * @param {Object} [options] - optional extra request options, merged into the request config
 * @returns {Promise<{
 *   success: boolean;
 *   errorCode?: number;
 *   errorMessage?: string;
 *   data?: {
 *     user: DATA.UserDefine;
 *     tokens: { access: DATA.TokenDefine; refresh: DATA.TokenDefine };
 *   };
 * }>} - typed API response
 *
 * @example
 * try {
 *   const res = await login({ username: 'alice', password: '123456' });
 *   if (res.success && res.data) {
 *     console.log('User:', res.data.user);
 *     console.log('Access token:', res.data.tokens.access.token);
 *   } else {
 *     console.error('Login failed:', res.errorMessage);
 *   }
 * } catch (err) {
 *   console.error('Network or unexpected error:', err);
 * }
 *
 * @complexity
 * - Time complexity: O(1) (single HTTP request)
 * - Space complexity: O(1)
 *
 * @sideEffects
 * - Triggers network call to backend `/api/v2/auth/login`
 * - May set authentication cookies if backend is configured with Set-Cookie
 *
 * @recommendations
 * - Always handle both `success=false` and thrown errors separately
 * - Store refresh tokens securely (e.g. HttpOnly cookie or secure storage)
 * - Do not log plaintext passwords in production
 */
export async function login(body: DATA.LoginParams, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: {
      user: DATA.UserDefine;
      tokens: { access: DATA.TokenDefine; refresh: DATA.TokenDefine };
    };
  }>('/api/v2/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...(options || {}),
    skipErrorHandler: true,
  });
}

/** Invalidate the refresh token server-side. Clearing localStorage is the caller's job. POST /api/v2/auth/logout */
export async function logout(options?: { [key: string]: any }) {
  const refreshToken = localStorage.getItem('refreshToken');
  return request<{ success: boolean; errorCode?: number; errorMessage?: string; data?: any }>(
    '/api/v2/auth/logout',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: { refreshToken },
      ...(options || {}),
      skipErrorHandler: true,
    },
  );
}

/** Create an account; the backend then emails a verification link. POST /api/v2/auth/register */
export async function register(body: DATA.RegisterParams, options?: { [key: string]: any }) {
  return request<{ success: boolean; errorCode?: number; errorMessage?: string; data?: boolean }>(
    '/api/v2/auth/register',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
      skipErrorHandler: true,
    },
  );
}

/** Redeem the token from the verification email. POST /api/v2/auth/verify-email */
export async function verifyEmail(body: { token: string }, options?: { [key: string]: any }) {
  return request<{ success: boolean; errorCode?: number; errorMessage?: string; data?: boolean }>(
    `/api/v2/auth/verify-email?token=${body.token}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      ...(options || {}),
      skipErrorHandler: true,
    },
  );
}

/** Trigger the password-reset email for an address. POST /api/v2/auth/forgot-password */
export async function forgotPassword(
  body: DATA.ForgotPasswordParams,
  options?: { [key: string]: any },
) {
  return request<{ success: boolean; errorCode?: number; errorMessage?: string; data?: boolean }>(
    '/api/v2/auth/forgot-password',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
}

/** Set a new password using the token from the reset email. POST /api/v2/auth/reset-password */
export async function resetPassword(
  token: string,
  body: { password: string },
  options?: { [key: string]: any },
) {
  return request<{ success: boolean; errorCode?: number; errorMessage?: string; data?: boolean }>(
    `/api/v2/auth/reset-password?token=${token}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    },
  );
}

/** Resolve the session owner. Failure here is what the auth gate in `src/app.tsx` reads as "logged out". GET /api/v2/users/me */
export async function getMe(options?: { [key: string]: any }) {
  // Read the access token straight from localStorage.
  const accessToken = localStorage.getItem('accessToken');

  if (!accessToken) {
    throw new Error('No access token found, please log in');
  }

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data: { user: DATA.UserDefine };
  }>('/api/v2/users/me', { method: 'GET', ...(options || {}) });
}

/** Exchange the stored refresh token for a fresh pair. Called only by the 401 recovery path in `src/requestErrorConfig.ts`. POST /api/v2/auth/refresh-tokens */
export async function refreshTokens(options?: { [key: string]: any }) {
  // Read the access token straight from localStorage.
  const refreshToken = localStorage.getItem('refreshToken');

  if (!refreshToken) {
    throw new Error('No refresh token found, please log in');
  }

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data: { access: DATA.TokenDefine; refresh: DATA.TokenDefine };
  }>('/api/v2/auth/refresh-tokens', {
    method: 'POST',
    ...(options || {}),
    data: { refreshToken },
    skipErrorHandler: true,
  });
}

// ============================================================================
// V2 API - the surface the current product runs on
// Roughly in dependency order: people, the reference pages that evidence
// them, search, then threads / questions / answers built on top.
// ============================================================================

// ============================================================================
// People (influencers)
// ============================================================================

/** Fetch one person with their reference pages resolved. GET /api/v2/influencers/:id */
export async function getV2InfluencerById(id: string, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.V2Influencer;
  }>(`/api/v2/influencers/${id}`, { method: 'GET', ...(options || {}) });
}

/** Permanently remove a person. Admin curation only. DELETE /api/v2/influencers/:id */
export async function deleteV2InfluencerById(id: string, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.V2Influencer;
  }>(`/api/v2/influencers/${id}`, { method: 'DELETE', ...(options || {}) });
}

/** Batch variant of `getV2InfluencerById`; ids go in the path as a comma-separated list. GET /api/v2/influencers/batch/:id */
export async function getV2InfluencersByIds(ids: string[], options?: { [key: string]: any }) {
  const idsStr = ids.join(',');
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.V2Influencer[];
  }>(`/api/v2/influencers/batch/${idsStr}`, { method: 'GET', ...(options || {}) });
}

/** Page through ids in one verification status. `lastId` is a cursor, not an offset. GET /api/v2/influencers/by-status/ids */
export async function getV2InfluencerIdsByStatus(
  vStatus: string,
  lastId?: string,
  options?: { [key: string]: any },
) {
  let url = `/api/v2/influencers/by-status/ids?vStatus=${vStatus}`;
  if (lastId) {
    url += `&lastId=${lastId}`;
  }

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: string[];
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Per-status totals, used to label the curation queue tabs. GET /api/v2/influencers/by-status/count */
export async function countV2InfluencersByStatus(options?: { [key: string]: any }) {
  const url = `/api/v2/influencers/by-status/count`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: {
      vStatus: string;
      count: number;
    }[];
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Ids this person was merged from, plus their own clones - the duplicate-resolution view. GET /api/v2/influencers/:id/clone-ids */
export async function getV2InfluencerCloneIds(id: string, options?: { [key: string]: any }) {
  const url = `/api/v2/influencers/${id}/clone-ids`;

  console.log(url);
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: { originalIds: string[]; originalCloneIds: string[] };
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Everything linked to a person (aliases and reference pages) in one round-trip, for the network graph. GET /api/v2/influencers/:id/network */
export async function getV2InfluencerNetwork(id: string, options?: { [key: string]: any }) {
  const url = `/api/v2/influencers/${id}/network`;

  console.log(url);
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: {
      influencerIds: string[];
      aliasNames?: DATA.AliasName[];
      homePage?: DATA.HomePage;
      wikiPage?: DATA.WikiPage;
      socialPages?: DATA.SocialPage[];
    };
  }>(url, { method: 'GET', ...(options || {}) });
}

// ============================================================================
// Reference pages - home / wiki / social / alias
// The four families are deliberately parallel: each has a by-count listing,
// a cursor-paged listing, a count, and a validity flag. Keep them in step.
// ============================================================================

/** Home pages ranked by how many people point at them - the busiest references first. GET /api/v2/references/home-pages/by-influencers-count */
export async function getV2HomePagesByInfluencersCount(options?: { [key: string]: any }) {
  const url = '/api/v2/references/home-pages/by-influencers-count';

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.HomePage[];
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Cursor-paged home pages, optionally narrowed to the invalid ones. GET /api/v2/references/home-pages${params.toString()  */
export async function getV2HomePages(
  isInvalid?: boolean,
  lastId?: string,
  options?: { [key: string]: any },
) {
  const params = new URLSearchParams();

  if (isInvalid === true || isInvalid === false) {
    params.append('isInvalid', String(isInvalid));
  }
  if (lastId) {
    params.append('lastId', lastId);
  }

  const url = `/api/v2/references/home-pages${params.toString() ? `?${params.toString()}` : ''}`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.HomePage[];
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Total home pages, matching the same `isInvalid` filter as the listing. GET /api/v2/references/home-pages/count */
export async function countV2HomePages(isInvalid?: boolean, options?: { [key: string]: any }) {
  let url = `/api/v2/references/home-pages/count`;

  if (isInvalid === true || isInvalid === false) {
    url += `?isInvalid=${isInvalid}`;
  }

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: number;
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Flag a home page valid/invalid globally, across every person referencing it. PUT /api/v2/references/home-pages/:id/:id */
export async function markHomePageValidity(
  homePageId: string,
  isInvalid: boolean,
  options?: { [key: string]: any },
) {
  const validity = isInvalid === true ? 'invalid' : 'valid';
  const url = `/api/v2/references/home-pages/${homePageId}/${validity}`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: {
      id: string;
      isInvalid: boolean;
    };
  }>(url, { method: 'PUT', ...(options || {}) });
}

/** Wiki pages ranked by referencing-people count. GET /api/v2/references/wiki-pages/by-influencers-count */
export async function getV2WikiPagesByInfluencersCount(options?: { [key: string]: any }) {
  const url = '/api/v2/references/wiki-pages/by-influencers-count';

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.WikiPage[];
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Cursor-paged wiki pages, optionally narrowed to the invalid ones. GET /api/v2/references/wiki-pages${params.toString()  */
export async function getV2WikiPages(
  isInvalid?: boolean,
  lastId?: string,
  options?: { [key: string]: any },
) {
  const params = new URLSearchParams();

  if (isInvalid === true || isInvalid === false) {
    params.append('isInvalid', String(isInvalid));
  }
  if (lastId) {
    params.append('lastId', lastId);
  }

  const url = `/api/v2/references/wiki-pages${params.toString() ? `?${params.toString()}` : ''}`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.WikiPage[];
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Total wiki pages, matching the same `isInvalid` filter as the listing. GET /api/v2/references/wiki-pages/count */
export async function countV2WikiPages(isInvalid?: boolean, options?: { [key: string]: any }) {
  let url = `/api/v2/references/wiki-pages/count`;

  if (isInvalid === true || isInvalid === false) {
    url += `?isInvalid=${isInvalid}`;
  }

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: number;
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Flag a wiki page valid/invalid globally. PUT /api/v2/references/wiki-pages/:id/:id */
export async function markWikiPageValidity(
  wikiPageId: string,
  isInvalid: boolean,
  options?: { [key: string]: any },
) {
  const validity = isInvalid === true ? 'invalid' : 'valid';
  const url = `/api/v2/references/wiki-pages/${wikiPageId}/${validity}`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: {
      id: string;
      isInvalid: boolean;
    };
  }>(url, { method: 'PUT', ...(options || {}) });
}

/** Social pages ranked by referencing-people count. GET /api/v2/references/social-pages/by-influencers-count */
export async function getV2SocialPagesByInfluencersCount(options?: { [key: string]: any }) {
  const url = '/api/v2/references/social-pages/by-influencers-count';

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.SocialPage[];
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Cursor-paged social pages, optionally narrowed to the invalid ones. GET /api/v2/references/social-pages${params.toString()  */
export async function getV2SocialPages(
  isInvalid?: boolean,
  lastId?: string,
  options?: { [key: string]: any },
) {
  const params = new URLSearchParams();

  if (isInvalid === true || isInvalid === false) {
    params.append('isInvalid', String(isInvalid));
  }
  if (lastId) {
    params.append('lastId', lastId);
  }

  const url = `/api/v2/references/social-pages${params.toString() ? `?${params.toString()}` : ''}`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.SocialPage[];
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Total social pages, matching the same `isInvalid` filter as the listing. GET /api/v2/references/social-pages/count */
export async function countV2SocialPages(isInvalid?: boolean, options?: { [key: string]: any }) {
  let url = `/api/v2/references/social-pages/count`;

  if (isInvalid === true || isInvalid === false) {
    url += `?isInvalid=${isInvalid}`;
  }

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: number;
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Flag a social page valid/invalid globally. PUT /api/v2/references/social-pages/:id/:id */
export async function markSocialPageValidity(
  socialPageId: string,
  isInvalid: boolean,
  options?: { [key: string]: any },
) {
  const validity = isInvalid === true ? 'invalid' : 'valid';
  const url = `/api/v2/references/social-pages/${socialPageId}/${validity}`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: {
      id: string;
      isInvalid: boolean;
    };
  }>(url, { method: 'PUT', ...(options || {}) });
}

/** Alias names ranked by referencing-people count. GET /api/v2/references/alias-names/by-influencers-count */
export async function getV2AliasNamesByInfluencersCount(options?: { [key: string]: any }) {
  const url = '/api/v2/references/alias-names/by-influencers-count';

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.AliasName[];
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Cursor-paged alias names, optionally narrowed to the fake ones. GET /api/v2/references/alias-names${params.toString()  */
export async function getV2AliasNames(
  isFake?: boolean,
  lastId?: string,
  options?: { [key: string]: any },
) {
  const params = new URLSearchParams();

  if (isFake === true || isFake === false) {
    params.append('isFake', String(isFake));
  }
  if (lastId) {
    params.append('lastId', lastId);
  }

  const url = `/api/v2/references/alias-names${params.toString() ? `?${params.toString()}` : ''}`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.AliasName[];
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Total alias names, matching the same `isFake` filter as the listing. GET /api/v2/references/alias-names/count */
export async function countV2AliasNames(isFake?: boolean, options?: { [key: string]: any }) {
  let url = `/api/v2/references/alias-names/count`;

  if (isFake === true || isFake === false) {
    url += `?isFake=${isFake}`;
  }

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: number;
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Flag an alias name as fake/genuine globally. PUT /api/v2/references/alias-names/:id/:id */
export async function markAliasNameFakeness(
  aliasNameId: string,
  isFake: boolean,
  options?: { [key: string]: any },
) {
  const fakeness = isFake === true ? 'fake' : 'unfake';
  const url = `/api/v2/references/alias-names/${aliasNameId}/${fakeness}`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: {
      id: string;
      isFake: boolean;
    };
  }>(url, { method: 'PUT', ...(options || {}) });
}

// ============================================================================
// Person <-> reference links
// Judging a *link*, not the referenced page. A page can be perfectly valid
// yet wrongly attributed to one person, which is what these endpoints fix.
// ============================================================================

/** Scoped counterpart of `markHomePageValidity`: judges the link between one person and one home page, leaving the page itself untouched elsewhere. PUT /api/v2/influencers/:id/:id/home-pages/:id */
export async function markInfluencerHomePageValidity(
  influencerId: string,
  homePageId: string,
  isInvalid: boolean,
  options?: { [key: string]: any },
) {
  const validity = isInvalid === true ? 'invalid' : 'valid';
  const url = `/api/v2/influencers/${influencerId}/${validity}/home-pages/${homePageId}/`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.InfluencerHomePage;
  }>(url, { method: 'PUT', ...(options || {}) });
}

/** Judge the link between one person and one wiki page only. PUT /api/v2/influencers/:id/:id/wiki-pages/:id */
export async function markInfluencerWikiPageValidity(
  influencerId: string,
  wikiPageId: string,
  isInvalid: boolean,
  options?: { [key: string]: any },
) {
  const validity = isInvalid === true ? 'invalid' : 'valid';
  const url = `/api/v2/influencers/${influencerId}/${validity}/wiki-pages/${wikiPageId}/`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.InfluencerWikiPage;
  }>(url, { method: 'PUT', ...(options || {}) });
}

/** Judge the link between one person and one social page only. PUT /api/v2/influencers/:id/:id/social-pages/:id */
export async function markInfluencerSocialPageValidity(
  influencerId: string,
  socialPageId: string,
  isInvalid: boolean,
  options?: { [key: string]: any },
) {
  const validity = isInvalid === true ? 'invalid' : 'valid';
  const url = `/api/v2/influencers/${influencerId}/${validity}/social-pages/${socialPageId}/`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.InfluencerSocialPage;
  }>(url, { method: 'PUT', ...(options || {}) });
}

/** Judge the link between one person and one alias name only. PUT /api/v2/influencers/:id/:id/alias-names/:id */
export async function markInfluencerAliasNameValidity(
  influencerId: string,
  aliasNameId: string,
  isInvalid: boolean,
  options?: { [key: string]: any },
) {
  const validity = isInvalid === true ? 'invalid' : 'valid';
  const url = `/api/v2/influencers/${influencerId}/${validity}/alias-names/${aliasNameId}/`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.InfluencerAliasName;
  }>(url, { method: 'PUT', ...(options || {}) });
}

// ============================================================================
// Search, similarity & following
// ============================================================================

/** Prefix matches for the search box, tuned for keystroke latency. GET /api/v2/influencers/by-autocomplete */
export async function autocompleteInfluencers(
  term: string,
  limit: number = 10,
  options?: { [key: string]: any },
) {
  const encoded = encodeURIComponent(term);
  const url = `/api/v2/influencers/by-autocomplete?term=${encoded}&limit=${limit}`;
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.V2Influencer[];
  }>(url, { method: 'GET', ...(options || {}) });
}

/** People best placed to answer about a topic - what seeds the recommender rail. GET /api/v2/influencers/best-recommenders */
export async function getBestRecommenders(
  term: string,
  limit: number = 10,
  options?: { [key: string]: any },
) {
  const encoded = encodeURIComponent(term);
  const url = `/api/v2/influencers/best-recommenders?term=${encoded}&limit=${limit}`;
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.V2Influencer[];
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Full search over people; slower and broader than `autocompleteInfluencers`. GET /api/v2/influencers/by-search */
export async function searchInfluencers(
  term: string,
  limit: number = 100,
  options?: { [key: string]: any },
) {
  const encoded = encodeURIComponent(term);
  const url = `/api/v2/influencers/by-search?term=${encoded}&limit=${limit}`;
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: string[];
  }>(url, { method: 'GET', ...(options || {}) });
}

/** People near this one in the embedding space, with their similarity scores. GET /api/v2/influencers/:id/similarities */
export async function getSimilarities(influencerId: string, options?: { [key: string]: any }) {
  const url = `/api/v2/influencers/${influencerId}/similarities`;
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: string[];
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Ids the signed-in user follows. Ids only - hydrate through `influencerContext`. GET /api/v2/influencers/by-following/ids */
export async function getFollowingInfluencerIds(options?: { [key: string]: any }) {
  const url = `/api/v2/influencers/by-following/ids`;
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: string[];
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Follow a person. POST /api/v2/influencers/:id/by-follow */
export async function followInfluencer(influencerId: string, options?: { [key: string]: any }) {
  const url = `/api/v2/influencers/${influencerId}/by-follow`;
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: any;
  }>(url, { method: 'POST', ...(options || {}) });
}

/** Unfollow a person. DELETE /api/v2/influencers/:id/by-follow */
export async function unfollowInfluencer(influencerId: string, options?: { [key: string]: any }) {
  const url = `/api/v2/influencers/${influencerId}/by-follow`;
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: string[];
  }>(url, { method: 'DELETE', ...(options || {}) });
}

// ============================================================================
// Threads
// ============================================================================

/** Open a conversation. A thread is the container; the first question is posted separately. POST /api/v2/threads */
export async function createThread(
  rawTitle?: string,
  influencerIds: string[] = [],
  searchMode?: 'fast' | 'deep',
  options?: { [key: string]: any },
) {
  const body: Record<string, any> = { influencerIds, source: 'manual' };
  if (rawTitle) {
    body.rawTitle = rawTitle;
  }
  if (searchMode) {
    body.searchMode = searchMode;
  }

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: { id: string };
  }>('/api/v2/threads', { method: 'POST', data: body, ...(options || {}) });
}

/** The user's threads, newest first, cursor-paged through `lastId`. GET /api/v2/threads */
export async function getThreads(
  before?: Date,
  limit: number = 10,
  options?: { [key: string]: any },
) {
  let url = `/api/v2/threads?limit=${limit}`;

  if (before) {
    url = `${url}&before=${encodeURIComponent(before.toISOString())}`;
  }

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.V2Thread[];
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Search the user's own threads by text. GET /api/v2/threads/action/by-search */
export async function searchThreads(query: string, options?: { [key: string]: any }) {
  const encoded = encodeURIComponent(query);
  const url = `/api/v2/threads/action/by-search?query=${encoded}&limit=10`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.V2Thread[];
  }>(url, {
    method: 'GET',
    ...(options || {}),
  });
}

/** Fetch one thread's metadata (not its questions). GET /api/v2/threads/:id */
export async function getThread(threadId: string, options?: { [key: string]: any }) {
  const url = `/api/v2/threads/${threadId}`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.V2Thread;
  }>(url, { method: 'GET', ...(options || {}) });
}

// ============================================================================
// Questions
// ============================================================================

/** Post a question into a thread. Answers stream in separately over SSE - the response only confirms the question was accepted. POST /api/v2/questions */
export async function createQuestion(
  threadId: string,
  rawTitle?: string,
  influencerIds?: string[],
  searchMode?: 'fast' | 'deep',
  options?: { [key: string]: any },
) {
  const url = '/api/v2/questions';

  const body: Record<string, any> = { threadId };
  if (rawTitle) {
    body.rawTitle = rawTitle;
  }
  if (influencerIds) {
    body.influencerIds = influencerIds;
  }
  if (searchMode) {
    body.searchMode = searchMode;
  }

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: { question: DATA.V2Question; candidates: DATA.V2Candidate[] };
  }>(url, { method: 'POST', data: body, ...(options || {}) });
}

/** Ask for a structured comparison of 2-3 already-cited sources, raised by the "Compare" action rather than typed by the user. POST /api/v2/questions/compare */
export async function createCompareQuestion(
  threadId: string,
  organicResultIds: string[],
  rawTitle: string,
  options?: { [key: string]: any },
) {
  const url = '/api/v2/questions/compare';

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: { question: DATA.V2Question; candidates: DATA.V2Candidate[] };
  }>(url, { method: 'POST', data: { threadId, organicResultIds, rawTitle }, ...(options || {}) });
}

/** Claim the SSE client id for a question, i.e. the handle used to subscribe to its answer stream. POST /api/v2/questions/:id/clientId */
export async function getQuestionClientId(questionId: string, options?: { [key: string]: any }) {
  const url = `/api/v2/questions/${questionId}/clientId`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: { clientId: string };
  }>(url, { method: 'POST', ...(options || {}) });
}

/** Create (or return the existing) `-2` geroscience-angle candidate for a question - Youth Button's click target. Creation only, does not trigger streaming - still needs getQuestionClientId + the question's own SSE reconnect for that. POST /api/v2/questions/:id/geroscience-angle */
export async function createGeroscienceAngleCandidate(
  questionId: string,
  options?: { [key: string]: any },
) {
  const url = `/api/v2/questions/${questionId}/geroscience-angle`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: { candidate: DATA.V2Candidate };
  }>(url, { method: 'POST', ...(options || {}) });
}

/** Every question in a thread, in order - the thread transcript. GET /api/v2/questions/by-thread */
export async function getQuestionsByThreadId(threadId: string, options?: { [key: string]: any }) {
  const url = `/api/v2/questions/by-thread?threadId=${threadId}`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.V2Question[];
  }>(url, { method: 'GET', ...(options || {}) });
}

// ============================================================================
// Candidates (answers) & their evidence
// ============================================================================

/** Answers proposed for a question. "Candidate" is the domain term for one answer with its sourcing. GET /api/v2/candidates/by-question */
export async function getCandidatesByQuestionId(
  questionId: string,
  options?: { [key: string]: any },
) {
  const url = `/api/v2/candidates/by-question?questionId=${questionId}`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.V2Candidate[];
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Fetch one answer. GET /api/v2/candidates/:id */
export async function getCandidateById(candidateId: string, options?: { [key: string]: any }) {
  const url = `/api/v2/candidates/${candidateId}`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.V2Candidate;
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Public read of an answer for the logged-out `/share/c/:candidateId` route. GET /api/v2/candidates/:id/by-shared */
export async function getSharedCandidateById(
  candidateId: string,
  options?: { [key: string]: any },
) {
  const url = `/api/v2/candidates/${candidateId}/by-shared`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: {
      candidate: DATA.V2Candidate;
      responder?: DATA.V2Influencer;
      influencers: DATA.V2Influencer[];
    };
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Answers attributed to a person, newest first. GET /api/v2/candidates/by-influencer */
export async function getCandidatesByInfluencerId(
  influencerId: string,
  before?: Date,
  limit: number = 10,
  options?: { [key: string]: any },
) {
  let url = `/api/v2/candidates/by-influencer?influencerId=${influencerId}&limit=${limit}`;
  if (before) {
    url = `${url}&before=${encodeURIComponent(before.toISOString())}`;
  }

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.V2Candidate[];
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Entities this person was mentioned alongside. GET /api/v2/influencers/:id/mentionedEntities */
export async function getMentionedEntites(
  influencerId: string,
  before?: string,
  limit: number = 10,
  options?: { [key: string]: any },
) {
  let url = `/api/v2/influencers/${influencerId}/mentionedEntities?limit=${limit}`;
  if (before) {
    url = `${url}&before=${before}`;
  }
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.V2MentionedEntity[];
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Most recent follow-up questions raised about a person. GET /api/v2/influencers/:id/latestFollowUps */
export async function getLatestFollowUps(influencerId?: string, options?: { [key: string]: any }) {
  const url = influencerId
    ? `/api/v2/influencers/${influencerId}/latestFollowUps`
    : `/api/v2/candidates/latestFollowUps`;
  // const url = `/api/v2/influencers/${influencerId}/latestFollowUps`;
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: string[];
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Sources backing what is known about a person. GET /api/v2/influencers/:id/referenceEntities */
export async function getReferenceEntitesByInfluencerId(
  influencerId: string,
  before?: string,
  limit: number = 10,
  options?: { [key: string]: any },
) {
  let url = `/api/v2/influencers/${influencerId}/referenceEntities?limit=${limit}`;
  if (before) {
    url = `${url}&before=${before}`;
  }
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.V2ReferenceEntity[];
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Batch answer fetch; ids go in the path as a comma-separated list. GET /api/v2/candidates/batch/:id */
export async function getCandidatesByIds(ids: string[], options?: { [key: string]: any }) {
  const idsStr = ids.join(',');
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.V2Candidate[];
  }>(`/api/v2/candidates/batch/${idsStr}`, { method: 'GET', ...(options || {}) });
}

// ============================================================================
// Bookmarks & feedback
// ============================================================================

/** Whether the signed-in user has bookmarked this answer. GET /api/v2/candidates/:id/bookmark */
export async function getBookmark(candidateId: string, options?: { [key: string]: any }) {
  const url = `/api/v2/candidates/${candidateId}/bookmark`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.V2Bookmark;
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Every answer the user bookmarked. GET /api/v2/candidates/my-bookmarks */
export async function getMyBookmarks(options?: { [key: string]: any }) {
  const url = `/api/v2/candidates/my-bookmarks`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.V2Bookmark[];
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Bookmark an answer. PUT /api/v2/candidates/:id/bookmark */
export async function addBookmark(candidateId: string, options?: { [key: string]: any }) {
  const url = `/api/v2/candidates/${candidateId}/bookmark`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.V2Bookmark;
  }>(url, { method: 'PUT', ...(options || {}) });
}

/** Remove a bookmark. DELETE /api/v2/candidates/:id/bookmark */
export async function removeBookmark(candidateId: string, options?: { [key: string]: any }) {
  const url = `/api/v2/candidates/${candidateId}/bookmark`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: boolean;
  }>(url, { method: 'DELETE', ...(options || {}) });
}

/** The user's existing reaction to an answer, if any. GET /api/v2/candidates/:id/feedback */
export async function getFeedback(candidateId: string, options?: { [key: string]: any }) {
  const url = `/api/v2/candidates/${candidateId}/feedback`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.V2Feedback;
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Record a thumbs-up/down style reaction on an answer. PUT /api/v2/candidates/:id/feedback */
export async function addFeedback(
  candidateId: string,
  reaction: DATA.ReactionType,
  options?: { [key: string]: any },
) {
  const url = `/api/v2/candidates/${candidateId}/feedback?reaction=${reaction}`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.V2Feedback;
  }>(url, { method: 'PUT', ...(options || {}) });
}

// ============================================================================
// OrganicResults
// ============================================================================

/** Structured extraction (authors/journal/clinical evidence/identifiers) for one cited source, generated on first request. GET /api/v2/organic-results/:id/extended */
export async function getOrganicResultExtendedContent(
  organicResultId: string,
  options?: { [key: string]: any },
) {
  const url = `/api/v2/organic-results/${organicResultId}/extended`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.V2OrganicResultExtended;
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Map expertise domains to the people who cover them. GET /api/v2/domains/influencer-ids/:id */
export async function getInfluencerIdsByDomainIds(ids: string[], options?: { [key: string]: any }) {
  const idsStr = ids.join(',');
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: { id: string; influencerId: string }[];
  }>(`/api/v2/domains/influencer-ids/${idsStr}`, { method: 'GET', ...(options || {}) });
}

/** Sources cited by one answer - what the citation chips resolve against. GET /api/v2/candidates/:id/reference-entities */
export async function getReferenceEntitiesByCandidateId(
  candidateId: string,
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.V2ReferenceEntity[];
  }>(`/api/v2/candidates/${candidateId}/reference-entities`, { method: 'GET', ...(options || {}) });
}

/** Synthesis (summary) generated for an answer. GET /api/v2/candidates/:id/syn */
export async function getSynByCandidateId(candidateId: string, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: {
      isFinished: boolean;
      referenceEntities: DATA.V2ReferenceEntity[];
      links: DATA.V2WebLink[];
      mentionedEntities: DATA.V2ProfileFigure[];
    };
  }>(`/api/v2/candidates/${candidateId}/syn`, { method: 'GET', ...(options || {}) });
}

/** People best placed to speak to this answer. GET /api/v2/candidates/:id/best-recommender-ids */
export async function getBestRecommenderIdsByCandidateId(
  candidateId: string,
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: string[];
  }>(`/api/v2/candidates/${candidateId}/best-recommender-ids`, {
    method: 'GET',
    ...(options || {}),
  });
}

/** Raw Google results for a person, used while curating. GET /api/v2/influencers/:id/googleSearch */
export async function getGoogleSearchByInfluencerId(
  influencerId: string,
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: any;
  }>(`/api/v2/influencers/${influencerId}/googleSearch`, { method: 'GET', ...(options || {}) });
}

// ============================================================================
// Curation - editing a person record
// ============================================================================

/** Set a person's portrait from a chosen URL. POST /api/v2/influencers/:id/updateAvatar */
export async function updateAvatar(
  influencerId: string,
  link: string,
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: { matchedCount: number };
  }>(`/api/v2/influencers/${influencerId}/updateAvatar`, {
    method: 'POST',
    data: { link },
    ...(options || {}),
  });
}

/** Attach or replace a person's home page. POST /api/v2/influencers/:id/updateHomePage */
export async function updateHomePage(
  influencerId: string,
  link: string,
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: { matchedCount: number };
  }>(`/api/v2/influencers/${influencerId}/updateHomePage`, {
    method: 'POST',
    data: { link },
    ...(options || {}),
  });
}

/** Attach or replace a person's wiki page. POST /api/v2/influencers/:id/updateWikiPage */
export async function updateWikiPage(
  influencerId: string,
  link: string,
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: { matchedCount: number };
  }>(`/api/v2/influencers/${influencerId}/updateWikiPage`, {
    method: 'POST',
    data: { link },
    ...(options || {}),
  });
}

/** Attach or replace one of a person's social profiles. POST /api/v2/influencers/:id/updateSocialPage */
export async function updateSocialPage(
  influencerId: string,
  link: string,
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: { matchedCount: number; socialMediaPages: DATA.V2SocialPage[] };
  }>(`/api/v2/influencers/${influencerId}/updateSocialPage`, {
    method: 'POST',
    data: { link },
    ...(options || {}),
  });
}

/** Set a person's country by human review. POST /api/v2/influencers/:id/updateCountry */
export async function updateCountry(
  influencerId: string,
  country: string,
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: { matchedCount: number };
  }>(`/api/v2/influencers/${influencerId}/updateCountry`, {
    method: 'POST',
    data: { country },
    ...(options || {}),
  });
}

/** Re-run backend validation on a record and return the refreshed result. POST /api/v2/influencers/:id/check */
export async function checkV2InfluencerById(
  influencerId: string,
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: { influencerId: string };
  }>(`/api/v2/influencers/${influencerId}/check`, {
    method: 'POST',
    ...(options || {}),
  });
}

// ============================================================================
// PRE-V2 SURFACE
// Older endpoints, kept because parts of the app still call them. Where a v2
// equivalent exists it is named in the doc comment; prefer that one in new
// code. Several entries here have no live caller at all and are marked as
// DEAD CODE.
// ============================================================================

/** Post a question addressed at a specific person. POST /api/v2/questions/self */
export async function createMyQuestion(
  body: DATA.CreateQuestionParams,
  options?: { [key: string]: any },
) {
  const requestBody = { ...body };

  if (!requestBody.lastQuestionId) {
    delete requestBody.lastQuestionId;
  }

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.Question;
  }>('/api/v2/questions/self', { method: 'POST', ...(options || {}), data: requestBody });
}

/** Withdraw a question addressed at a person. DELETE /api/v2/questions/self/:id */
export async function deleteMyQuestion(influencerId: string, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.Question;
  }>(`/api/v2/questions/self/${influencerId}`, { method: 'DELETE', ...(options || {}) });
}

/** The user's own questions, cursor-paged and optionally filtered by responder. GET /api/v2/questions/self */
export async function getMyQuestions(
  responder?: string,
  beforeObjectId?: string,
  options?: { [key: string]: any },
) {
  let url = '/api/v2/questions/self';
  if (responder) {
    url += `?responder=${responder}`;
    if (beforeObjectId) {
      url += `&beforeObjectId=${beforeObjectId}`;
    }
  } else if (beforeObjectId) {
    url += `?beforeObjectId=${beforeObjectId}`;
  }
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.Question[];
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Total of the user's own questions, matching the same responder filter. GET /api/v2/questions/self/count */
export async function countMyQuestions(responder?: string, options?: { [key: string]: any }) {
  let url = '/api/v2/questions/self/count';
  if (responder) {
    url += `?responder=${responder}`;
  }
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: { total: number };
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Batch tag lookup. POST because the id list can outgrow a URL. POST /api/v2/tags/ids */
export async function getTagsByIds(ids: string[], options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.Tag[];
  }>('/api/v2/tags/ids', { method: 'POST', ...(options || {}), data: { ids } });
}

/** Batch person lookup. POST because the id list can outgrow a URL. POST /api/v2/influencers/ids */
export async function getInfluencersByIds(ids: string[], options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.Influencer[];
  }>('/api/v2/influencers/ids', { method: 'POST', ...(options || {}), data: { ids } });
}

/** People carrying any of the given tags. POST /api/v2/influencers/filter-by-tag-ids */
export async function getInfluencersByTagIds(ids: string[], options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.Influencer[];
  }>('/api/v2/influencers/filter-by-tag-ids', {
    method: 'POST',
    ...(options || {}),
    data: { ids },
  });
}

/** Ids of the people the user favourited. GET /api/v2/influencers/favorites/ids */
export async function getfavoritedInfluenceIds(options?: { [key: string]: any }) {
  return request<{ success: boolean; errorCode?: number; errorMessage?: string; data?: string[] }>(
    '/api/v2/influencers/favorites/ids',
    { method: 'GET', ...(options || {}) },
  );
}

/** Favourite a person. POST /api/v2/influencers/:id/favorite */
export async function favoriteInfluencer(id: string, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: { influencer: string };
  }>(`/api/v2/influencers/${id}/favorite`, { method: 'POST', ...(options || {}) });
}

/** Un-favourite a person. DELETE /api/v2/influencers/:id/favorite */
export async function unfavoriteInfluencer(id: string, options?: { [key: string]: any }) {
  return request<{ success: boolean; errorCode?: number; errorMessage?: string; data?: any }>(
    `/api/v2/influencers/${id}/favorite`,
    { method: 'DELETE', ...(options || {}) },
  );
}

/** DEAD CODE - no live caller; the `/gpt/` proxy is commented out. Superseded by `newUpdateInfluencerProfile`. GET /gpt/update-profile/:id */
export async function updateInfluencerProfile(id: string, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: { profile: string; title: string };
  }>(`/gpt/update-profile/${id}`, { method: 'GET', ...(options || {}), skipErrorHandler: true });
}

/** DEAD CODE - no live caller; the `/gpt/` proxy is commented out. GET /gpt/get-leading-questions/:id */
export async function getLeadingQuestions(id: string, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: { leading: { timestamp: number; questions: string[] } };
  }>(`/gpt/get-leading-questions/${id}`, {
    method: 'GET',
    ...(options || {}),
    skipErrorHandler: true,
  });
}

/** DEAD CODE - no live caller. GET /api/v2/follow-up-questions/self */
export async function getFollowUpQuestions(responder?: string, options?: { [key: string]: any }) {
  let url = '/api/v2/follow-up-questions/self';
  if (responder) {
    url += `?responder=${responder}`;
  }

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.FollowUpQuestion[];
  }>(url, { method: 'GET', ...(options || {}), skipErrorHandler: true });
}

/** DEAD CODE - no live caller. GET /api/v2/follow-up-questions/by-question */
export async function getFollowUpQuestionsByQuestion(
  questionId: string,
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.FollowUpQuestion[];
  }>(`/api/v2/follow-up-questions/by-question?question=${questionId}`, {
    method: 'GET',
    ...(options || {}),
    skipErrorHandler: true,
  });
}

/** DEAD CODE - no live caller. POST /api/v2/follow-up-questions/self/mark/:id */
export async function maskFollowUpQuestion(
  followUpQuestionId: string,
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.FollowUpQuestion;
  }>(`/api/v2/follow-up-questions/self/mark/${followUpQuestionId}`, {
    method: 'POST',
    ...(options || {}),
    skipErrorHandler: true,
  });
}

/** DEAD CODE - no live caller. POST /api/v2/follow-up-questions/self/unmark/:id */
export async function unmaskFollowUpQuestion(
  followUpQuestionId: string,
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.FollowUpQuestion;
  }>(`/api/v2/follow-up-questions/self/unmark/${followUpQuestionId}`, {
    method: 'POST',
    ...(options || {}),
    skipErrorHandler: true,
  });
}

/** DEAD CODE - no live caller. DELETE /api/v2/follow-up-questions/self/:id */
export async function deleteFollowUpQuestion(
  followUpQuestionId: string,
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.FollowUpQuestion;
  }>(`/api/v2/follow-up-questions/self/${followUpQuestionId}`, {
    method: 'DELETE',
    ...(options || {}),
    skipErrorHandler: true,
  });
}

/** DEAD CODE - no live caller. POST /api/v2/questions/self/rate/:id */
export async function rateMyQuestion(
  influencerId: string,
  rating: number,
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.Question;
  }>(`/api/v2/questions/self/rate/${influencerId}`, {
    method: 'POST',
    ...(options || {}),
    data: { rating },
    skipErrorHandler: true,
  });
}

/** DEAD CODE - no live caller. GET /api/v2/influencers/:id/avatars */
export async function getInfluencerAvatars(influencerId: string, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.ImageItem[];
  }>(`/api/v2/influencers/${influencerId}/avatars`, {
    method: 'GET',
    ...(options || {}),
    skipErrorHandler: true,
  });
}

/** DEAD CODE - no live caller. GET /api/v2/influencers/:id/profile-images */
export async function getInfluencerProfileImages(
  influencerId: string,
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.ProfileImage[];
  }>(`/api/v2/influencers/${influencerId}/profile-images`, {
    method: 'GET',
    ...(options || {}),
    skipErrorHandler: true,
  });
}

/** DEAD CODE - no live caller. POST /api/v2/influencers/:id/profile-images */
export async function updateInfluencerProfileImage(
  influencerId: string,
  link: string,
  options?: { [key: string]: any },
) {
  return request<{ success: boolean; errorCode?: number; errorMessage?: string }>(
    `/api/v2/influencers/${influencerId}/profile-images`,
    { method: 'POST', ...(options || {}), data: { link }, skipErrorHandler: true },
  );
}

/** DEAD CODE - no live caller. POST /api/v2/influencers/:id/avatars */
export async function updateInfluencerAvatar(
  influencerId: string,
  link: string,
  options?: { [key: string]: any },
) {
  return request<{ success: boolean; errorCode?: number; errorMessage?: string }>(
    `/api/v2/influencers/${influencerId}/avatars`,
    { method: 'POST', ...(options || {}), data: { link }, skipErrorHandler: true },
  );
}

/** People similar to this one, hydrated (contrast `getSimilarities`, which returns scores). GET /api/v2/influencers/:id/similars */
export async function getSimilarInfluencers(id: string, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.Influencer[];
  }>(`/api/v2/influencers/${id}/similars`, {
    method: 'GET',
    ...(options || {}),
    skipErrorHandler: true,
  });
}

/** DEAD CODE - no live caller. GET /api/v2/influencers/relatedWith/:id */
export async function getRelatedInfluencers(questionId: string, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.Influencer[];
  }>(`/api/v2/influencers/relatedWith/${questionId}`, {
    method: 'GET',
    ...(options || {}),
    skipErrorHandler: true,
  });
}

/** DEAD CODE - no live caller. POST /api/v2/influencers/autocomplete */
export async function getAutocompleteInfluencers(query: string, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.Influencer[];
  }>('/api/v2/influencers/autocomplete', {
    method: 'POST',
    ...(options || {}),
    data: { query },
    skipErrorHandler: true,
  });
}

/** Fetch one person. Older, flatter shape than `getV2InfluencerById`. GET /api/v2/influencers/:id */
export async function getInfluencer(id: string, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.Influencer;
  }>(`/api/v2/influencers/${id}`, { method: 'GET', ...(options || {}), skipErrorHandler: true });
}

/** Delete a person via the pre-v2 endpoint. DELETE /api/v2/influencers/:id */
export async function deleteInfluencer(id: string, options?: { [key: string]: any }) {
  return request<{ success: boolean; errorCode?: number; errorMessage?: string; data?: boolean }>(
    `/api/v2/influencers/${id}`,
    { method: 'DELETE', ...(options || {}) },
  );
}

/** Merge duplicates: redirect `fromId` at `toId`, so the former resolves to the latter. POST /api/v2/influencers/point-to/:id/:id */
export async function pointTo(fromId: string, toId: string, options?: { [key: string]: any }) {
  return request<{ success: boolean; errorCode?: number; errorMessage?: string; data?: boolean }>(
    `/api/v2/influencers/point-to/${fromId}/${toId}`,
    { method: 'POST', ...(options || {}) },
  );
}

/** DEAD CODE - no live caller. GET /api/v2/follow-up-questions/by-influencer/:id */
export async function getFollowUpQuestionsByInfluencer(
  influencerId: string,
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.FollowUpQuestion[];
  }>(`/api/v2/follow-up-questions/by-influencer/${influencerId}`, {
    method: 'GET',
    ...(options || {}),
    skipErrorHandler: true,
  });
}

/** DEAD CODE - no live caller. GET /api/v2/questions/by-influencer/:id */
export async function getQuestionsByInfluencer(
  influencerId: string,
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.Question[];
  }>(`/api/v2/questions/by-influencer/${influencerId}`, {
    method: 'GET',
    ...(options || {}),
    skipErrorHandler: true,
  });
}

/** DEAD CODE - no live caller. GET /api/v2/questions/current/:id */
export async function getQuestionById(questionId: string, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.Question;
  }>(`/api/v2/questions/current/${questionId}`, {
    method: 'GET',
    ...(options || {}),
    skipErrorHandler: true,
  });
}

/** Most recently added people. GET /api/v2/influencers/latests */
export async function getLatestInfluencers(options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.V2Influencer[];
  }>('/api/v2/influencers/latests', { method: 'GET', ...(options || {}), skipErrorHandler: true });
}

/** DEAD CODE - no live caller. POST /api/v2/questions/self/like/:id */
export async function likeMyQuestion(questionId: string, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.Question;
  }>(`/api/v2/questions/self/like/${questionId}`, {
    method: 'POST',
    ...(options || {}),
    skipErrorHandler: true,
  });
}

/** DEAD CODE - no live caller. POST /api/v2/questions/self/unlike/:id */
export async function unlikeMyQuestion(questionId: string, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.Question;
  }>(`/api/v2/questions/self/unlike/${questionId}`, {
    method: 'POST',
    ...(options || {}),
    skipErrorHandler: true,
  });
}

/** The user's bookmarked questions, cursor-paged. GET /api/v2/questions/self/bookmark */
export async function getBookmarkedMyQuestions(
  beforeObjectId?: string,
  options?: { [key: string]: any },
) {
  let url = '/api/v2/questions/self/bookmark';
  if (beforeObjectId) {
    url += `?beforeObjectId=${beforeObjectId}`;
  }
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.Question[];
  }>(url, { method: 'GET', ...(options || {}) });
}

/** DEAD CODE - no live caller. POST /api/v2/questions/self/bookmark/:id */
export async function bookmarkMyQuestion(questionId: string, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.Question;
  }>(`/api/v2/questions/self/bookmark/${questionId}`, {
    method: 'POST',
    ...(options || {}),
    skipErrorHandler: true,
  });
}

/** DEAD CODE - no live caller. DELETE /api/v2/questions/self/bookmark/:id */
export async function unbookmarkMyQuestion(questionId: string, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.Question;
  }>(`/api/v2/questions/self/bookmark/${questionId}`, {
    method: 'DELETE',
    ...(options || {}),
    skipErrorHandler: true,
  });
}

/** DEAD CODE - no live caller. GET /api/v2/influencers/by-status/:id/count */
export async function countInfluencerByStatus(vStatus: string, options?: { [key: string]: any }) {
  return request<{ success: boolean; errorCode?: number; errorMessage?: string; data?: number }>(
    `/api/v2/influencers/by-status/${vStatus}/count`,
    { method: 'GET', ...(options || {}), skipErrorHandler: true },
  );
}

/** Move a record through the verification workflow. PUT /api/v2/influencers/:id/status */
export async function updateInfluencerStatus(
  influencerId: string,
  vStatus: string,
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: { influencerId: string; status: string };
  }>(`/api/v2/influencers/${influencerId}/status`, {
    method: 'PUT',
    ...(options || {}),
    data: { status: vStatus },
    skipErrorHandler: true,
  });
}

/** Server-side profile regeneration. Supersedes `updateInfluencerProfile`, which went through the `/gpt/` proxy. POST /api/v2/influencers/:id/update-profile */
export async function newUpdateInfluencerProfile(
  influencerId: string,
  options?: { [key: string]: any },
) {
  const url = `/api/v2/influencers/${influencerId}/update-profile`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.Influencer;
  }>(url, { method: 'POST', ...(options || {}) });
}

/** DEAD CODE - no live caller. GET /api/v2/influencers/by-status/:id/query */
export async function queryInfluencerByStatus(
  vStatus: string,
  beforeObjectId?: string,
  options?: { [key: string]: any },
) {
  let url = `/api/v2/influencers/by-status/${vStatus}/query`;
  if (beforeObjectId) {
    url += `?beforeObjectId=${beforeObjectId}`;
  }
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.Influencer[];
  }>(url, { method: 'GET', ...(options || {}) });
}

/** Mark a record as verified by a human curator. POST /api/v2/influencers/:id/humanVerified */
export async function humanVerifyInfluencer(
  influencerId: string,
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.Influencer;
  }>(`/api/v2/influencers/${influencerId}/humanVerified`, {
    method: 'POST',
    ...(options || {}),
    skipErrorHandler: true,
  });
}

/** Reject a record, taking it out of the curation queue without deleting it. POST /api/v2/influencers/:id/discarded */
export async function discardInfluencer(influencerId: string, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.Influencer;
  }>(`/api/v2/influencers/${influencerId}/discarded`, {
    method: 'POST',
    ...(options || {}),
    skipErrorHandler: true,
  });
}

/** DEAD CODE - no live caller. GET /api/v2/influencers/:id/social_medias */
export async function getInfluencerSocialMediaProfile(
  influencerId: string,
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.InfluencerSocialMediaProfile;
  }>(`/api/v2/influencers/${influencerId}/social_medias`, {
    method: 'GET',
    ...(options || {}),
    skipErrorHandler: true,
  });
}

/** DEAD CODE - no live caller. GET /api/v2/tags/latest */
export async function getLatestTags(beforeObjectId?: string, options?: { [key: string]: any }) {
  let url = `/api/v2/tags/latest`;
  if (beforeObjectId) {
    url += `?beforeObjectId=${beforeObjectId}`;
  }

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.Tag[];
  }>(url, { method: 'GET', ...(options || {}), skipErrorHandler: true });
}

/** DEAD CODE - no live caller. GET /api/v2/tags/favorites/ids */
export async function getfavoritedTagIds(options?: { [key: string]: any }) {
  return request<{ success: boolean; errorCode?: number; errorMessage?: string; data?: string[] }>(
    '/api/v2/tags/favorites/ids',
    { method: 'GET', ...(options || {}) },
  );
}

/** DEAD CODE - no live caller. POST /api/v2/tags/:id/favorite */
export async function favoriteTag(id: string, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: { influencer: string };
  }>(`/api/v2/tags/${id}/favorite`, { method: 'POST', ...(options || {}) });
}

/** DEAD CODE - no live caller. DELETE /api/v2/tags/:id/favorite */
export async function unfavoriteTag(id: string, options?: { [key: string]: any }) {
  return request<{ success: boolean; errorCode?: number; errorMessage?: string; data?: any }>(
    `/api/v2/tags/${id}/favorite`,
    { method: 'DELETE', ...(options || {}) },
  );
}

/** DEAD CODE - no live caller. GET /api/v2/trending/entities */
export async function getTrendingEntities(options?: { [key: string]: any }) {
  let url = `/api/v2/trending/entities`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.GoogleTrendingEntity[];
  }>(url, { method: 'GET', ...(options || {}), skipErrorHandler: true });
}

/** DEAD CODE - no live caller. GET /api/v2/trending/self/entries */
export async function getGoogleTrendingEntries(options?: { [key: string]: any }) {
  let url = `/api/v2/trending/self/entries`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: {
      results: DATA.GoogleTrendingEntry[];
      page: number;
      limit: number;
      totalPages: number;
      totalResults: number;
    };
  }>(url, { method: 'GET', ...(options || {}), skipErrorHandler: true });
}

/** DEAD CODE - no live caller. GET /api/v2/trending/self/setting */
export async function getGoogleTrendingSetting(options?: { [key: string]: any }) {
  const url = `/api/v2/trending/self/setting`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.GoogleTrendingSetting;
  }>(url, { method: 'GET', ...(options || {}), skipErrorHandler: true });
}

/** DEAD CODE - no live caller. POST /api/v2/trending/self/setting */
export async function postGoogleTrendingSetting(
  locations: string[],
  categories: string[],
  options?: { [key: string]: any },
) {
  const url = `/api/v2/trending/self/setting`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.GoogleTrendingSetting;
  }>(url, {
    method: 'POST',
    data: { locations, categories },
    ...(options || {}),
    skipErrorHandler: true,
  });
}

/** DEAD CODE - no live caller. GET /api/v2/trending/entry/:id/news */
export async function getGoogleTrendingNews(id: string, options?: { [key: string]: any }) {
  const url = `/api/v2/trending/entry/${id}/news`;

  return request<{
    success: boolean;
    errorCode?: number;
    errorMessage?: string;
    data?: DATA.NewsItem[];
  }>(url, { method: 'GET', ...(options || {}), skipErrorHandler: true });
}

/** DEAD CODE - no live caller. POST /api/v2/questions/self/share/:id */
export async function shareMyQuestion(questionId: string, options?: { [key: string]: any }) {
  return request<{ success: boolean; errorCode?: number; errorMessage?: string; data?: boolean }>(
    `/api/v2/questions/self/share/${questionId}`,
    { method: 'POST', ...(options || {}), skipErrorHandler: true },
  );
}
